import React, { useEffect, useState, useCallback } from "react";
import classnames from "classnames";
import {
  Button,
  CustomInput,
  Col,
  Form,
  FormGroup,
  ListGroup,
  ListGroupItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
} from "reactstrap";
import { usersApi } from "../../../services/userServices";

const WAITING_FOR_ASSIGNMENT_STATUS = 1;
const WAITING_FOR_DETAILER_STATUS = 2;
const DETAILING_IN_PROGRESS_STATUS = 3;
const WAITING_FOR_CHECKING_STATUS = 4;
const CHECKING_IN_PROGRESS_STATUS = 5;
const CHECKING_COMPLETE_STATUS = 6;
const REVISION_GENERATED_STATUS = 7;
const WAITING_FOR_SCRUBBING_STATUS = 8;
const SCRUBBING_STATUS = 9;
const WAITING_FOR_LOOK_OVER_STATUS = 10;
const LOOK_OVER_STATUS = 11;

const SEQUENCE_STATUSES = [
  { status: "Waiting for Assignment", value: WAITING_FOR_ASSIGNMENT_STATUS },
  { status: "Waiting for Detailer", value: WAITING_FOR_DETAILER_STATUS },
  { status: "Detailing in Progress", value: DETAILING_IN_PROGRESS_STATUS },
  { status: "Waiting for Checking", value: WAITING_FOR_CHECKING_STATUS },
  { status: "Checking in Progress", value: CHECKING_IN_PROGRESS_STATUS },
  { status: "Waiting for Scrubbing", value: WAITING_FOR_SCRUBBING_STATUS },
  { status: "Scrubbing", value: SCRUBBING_STATUS },
  { status: "Waiting for Look Over", value: WAITING_FOR_LOOK_OVER_STATUS },
  { status: "Look Over", value: LOOK_OVER_STATUS },
  { status: "Ready for Review", value: CHECKING_COMPLETE_STATUS },
  { status: "Revision Generated", value: REVISION_GENERATED_STATUS },
];

const SequenceDetailsModal = ({
  sequence,
  detailModal,
  onSubmit,
  onClear,
  readOnly,
  setInformationModal = () => {},
  checkerId,
  setCheckerId = () => {},
  detailerId,
  setDetailerId = () => {},
  enableUpdate,
  statusId,
  setStatusId = () => {},
}) => {
  const [users, setUsers] = useState();
  const [checkers, setCheckers] = useState([]);
  const [detailers, setDetailers] = useState([]);
  const [selectedTab, setSelectedTab] = useState();
  const detailer =
    detailerId ||
    `${sequence?.detailer?.firstName} ${sequence?.detailer?.lastName}`;
  const checker =
    checkerId ||
    `${sequence?.checker?.firstName} ${sequence?.checker?.lastName}`;

  const optionChange = useCallback(
    (userId, isDetailer, usersList = users) => {
      if (!usersList) return;

      const usersFiltered = usersList.filter((user) => user?.id !== +userId);
      if (isDetailer) {
        setCheckers(usersFiltered);
        setDetailerId(userId || null);
      } else {
        setDetailers(usersFiltered);
        setCheckerId(userId || null);
      }
    },
    [users, setCheckerId, setDetailerId],
  );
  const assignmentValidation = !!(
    (typeof checkerId !== "undefined" ? checkerId : sequence?.checker) &&
    checkerId !== "-" &&
    (typeof detailerId !== "undefined" ? detailerId : sequence?.detailer) &&
    detailerId !== "-"
  );
  // Load users only once
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (detailModal && !users) {
          const data = await usersApi.getUsers({ pageSize: 10000 });
          setUsers(data.data);
        }
      } catch (err) {
        if (typeof setInformationModal === "function") {
          setInformationModal({
            isOpen: true,
            title: "Error",
            body: "There was an error loading users.",
          });
        }
      }
    };

    fetchUsers();
  }, [detailModal, users, setInformationModal]);

  // Initialize user options when users are loaded or props change
  useEffect(() => {
    if (users && users.length > 0) {
      optionChange(detailerId, true, users);
      optionChange(checkerId, false, users);
    }
  }, [users, detailerId, checkerId, optionChange]);

  // Handle sequence revisions tab selection
  useEffect(() => {
    if (!selectedTab && sequence?.revisions?.length) {
      setSelectedTab(sequence?.revisions[0]?.id);
    }
  }, [selectedTab, sequence?.revisions]);

  return (
    <Modal isOpen={detailModal} className="modal-sm">
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Row className="mt-2 mx-2">
            <ListGroup className="w-100">
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Customer</span>
                <span>{sequence?.job?.customer?.name || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Job#</span>
                <span>{sequence?.job?.jobNumber || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Project Manager</span>
                <span>
                  {sequence?.job?.manager
                    ? `${sequence?.job?.manager?.firstName} ${sequence?.job?.manager?.lastName}`
                    : "-"}
                </span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Due Date</span>
                <span>{sequence?.revisionDate || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Job Desc.</span>
                <span>{sequence?.description || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">PM Notes</span>
                <span>{sequence?.notes || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Review Process</span>
                <span>{sequence?.reviewProcess?.description || "-"}</span>
              </ListGroupItem>
              <ListGroupItem>
                <FormGroup className="w-100 d-flex justify-content-between mb-0">
                  <Label className="mb-0 font-weight-bold" for="detailer">
                    Detailer
                  </Label>
                  {readOnly ? (
                    <span>{detailer || "-"}</span>
                  ) : (
                    <CustomInput
                      className="d-inline-block w-auto"
                      type="select"
                      id="detailer"
                      value={detailerId || ""}
                      onChange={(e) => optionChange(e.target.value, true)}
                    >
                      <option value="">Select Detailer</option>
                      {detailers
                        .filter((detailer) => detailer?.id !== +checkerId)
                        .map((detailer) => (
                          <option
                            value={detailer.id}
                            key={`detailer-${detailer.id}`}
                          >{`${detailer?.firstName} ${detailer?.lastName}`}</option>
                        ))}
                    </CustomInput>
                  )}
                </FormGroup>
              </ListGroupItem>
              <ListGroupItem>
                <FormGroup className="w-100 d-flex justify-content-between mb-0">
                  <Label className="mb-0 font-weight-bold" for="checker">
                    Checker
                  </Label>
                  {readOnly ? (
                    <span>{checker || "-"}</span>
                  ) : (
                    <CustomInput
                      className="d-inline-block w-auto"
                      type="select"
                      id="checker"
                      value={checkerId || ""}
                      onChange={(e) => optionChange(e.target.value)}
                    >
                      <option value="">Select Checker</option>
                      {checkers
                        .filter((checker) => checker?.id !== +detailerId)
                        .map((checker) => (
                          <option
                            value={checker.id}
                            key={`checker-${checker.id}`}
                          >{`${checker?.firstName} ${checker?.lastName}`}</option>
                        ))}
                    </CustomInput>
                  )}
                </FormGroup>
              </ListGroupItem>
              {enableUpdate ? (
                <ListGroupItem>
                  <FormGroup className="w-100 d-flex justify-content-between mb-0">
                    <Label className="mb-0 font-weight-bold" for="status">
                      Status
                    </Label>
                    <CustomInput
                      className="d-inline-block w-auto"
                      type="select"
                      id="status"
                      value={statusId || ""}
                      onChange={(e) => setStatusId(+e.target.value)}
                    >
                      <option value={""} disabled>
                        Select A Status
                      </option>
                      {SEQUENCE_STATUSES.filter(
                        (status) =>
                          assignmentValidation || status.value === statusId,
                      ).map((item) => (
                        <option value={item.value} key={`item-${item.value}`}>
                          {item.status}
                        </option>
                      ))}
                    </CustomInput>
                  </FormGroup>
                </ListGroupItem>
              ) : null}
            </ListGroup>
            {sequence?.revisions?.length ? (
              <div className="flex-grow-1 overflow-x-auto">
                <Row className="m-0">
                  <div>
                    <Nav
                      tabs
                      className="text-center flex-nowrap job-details-nav cursor-pointer mt-2"
                    >
                      <NavItem key={`link-${sequence.revisions[0].id}`}>
                        <NavLink
                          className={classnames({
                            active: selectedTab === sequence.revisions[0].id,
                          })}
                          onClick={() =>
                            setSelectedTab(sequence.revisions[0].id)
                          }
                        >
                          {sequence.revisions[0].name}
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <TabContent activeTab={selectedTab}>
                      <TabPane
                        tabId={sequence.revisions[0].id}
                        key={`tab-pane-${sequence.revisions[0].id}`}
                      >
                        <Card className="box-shadow-none border-left border-bottom border-right mb-0">
                          <CardBody className="px-2 pb-2">
                            <strong className="ml-2">Notes: </strong>
                            {sequence.revisions[0].notes}
                          </CardBody>
                        </Card>
                      </TabPane>
                    </TabContent>
                  </div>
                </Row>
              </div>
            ) : null}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Col>
            <Row>
              {readOnly && !enableUpdate ? (
                <Button
                  color={"secondary"}
                  onClick={() => {
                    setSelectedTab();
                    onClear();
                  }}
                >
                  Close
                </Button>
              ) : (
                <div className="d-flex justify-content-between w-100">
                  <Button
                    color={"secondary"}
                    onClick={() => {
                      setSelectedTab();
                      onClear();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button color={"primary"} type="submit">
                    Save
                  </Button>
                </div>
              )}
            </Row>
          </Col>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SequenceDetailsModal;
